import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
    EmptyManualIntegration,
    IntegrationCompany,
    integrationsByCompany,
    LocalEditableIntegration,
} from "../../../Everou/_modelsTS/Integration";
import { MainPageWithButtons } from "../../../Everou/_components";
import { removeFromArr } from "../../../Common/_utils/utils";
import { Selectors } from "../../../Everou/_reducers/app.reducer";
import { PMSIntegrationsActions } from "../../_actions/PMSIntegrationsActions";
import { AddIntegrationButton } from "../_components/AddIntegrationButton";
import { useAccountDraft } from "../_hooks/useAccountDraft";
import { PMSManualIntegrationInput } from "../_components/PMSManualIntegrationInput";

/**
 * The Manual "PMS": no account, no credentials, nothing to connect to.
 *
 * Activating it is what gives a location an integration to hang hand-typed
 * reservations from (PUT pms/reservation has always required one). Several are
 * allowed on purpose -- they are labels, not accounts ("Reservas directas",
 * "Estancias de propietario") -- which is why the name is the one required
 * field: nothing else tells two of them apart in the picker.
 */
export function PMSManualIntegration() {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const serverIntegrations = useSelector(Selectors.getPMSIntegrationsInPanel);
    const accountDraft = useAccountDraft(IntegrationCompany.MANUAL);

    const [integrationsLocal, setIntegrationsLocal] = useState<LocalEditableIntegration[]>(
        () => integrationsByCompany(serverIntegrations, IntegrationCompany.MANUAL, accountDraft),
    );

    useEffect(() => {
        setIntegrationsLocal(
            integrationsByCompany(serverIntegrations, IntegrationCompany.MANUAL, accountDraft));
    }, [serverIntegrations, accountDraft]);

    // Same rule as the other multi-account platforms: no second empty form until
    // the one on screen has been saved.
    const canAddOnMore = useMemo(
        () => integrationsLocal.length === 0 || !!integrationsLocal[integrationsLocal.length - 1].uid,
        [integrationsLocal]);

    const addIntegration = useCallback(() => {
        if (canAddOnMore) {
            setIntegrationsLocal([...integrationsLocal, EmptyManualIntegration]);
        }
    }, [canAddOnMore, integrationsLocal]);

    const onDeleteIntegration = (index: number) => {
        const integration = integrationsLocal[index];
        setIntegrationsLocal(removeFromArr<LocalEditableIntegration>(index, integrationsLocal));
        if (integration.uid) {
            dispatch(PMSIntegrationsActions.deleteIntegration(integration.uid));
        }
    };

    const toggleEditable = (index: number) => {
        setIntegrationsLocal(integrationsLocal.map((integration, i) =>
            i === index ? { ...integration, editable: !integration.editable } : integration));
    };

    // The name only earns its "required" from the SECOND account on: the first
    // has nothing to be confused with (the backend draws the same line). Counts
    // the SAVED ones other than this card, so the second form asks for a name
    // while it is still being filled in.
    const savedCount = integrationsLocal.filter(({ uid }) => !!uid).length;

    const body = (
        <div className="c-grid c-grid--small rounded-bottom">
            {integrationsLocal.map((integration, index) => (
                <PMSManualIntegrationInput
                    key={integration.uid || "new"}
                    index={index}
                    integration={integration}
                    nameRequired={savedCount > (integration.uid ? 1 : 0)}
                    onDelete={onDeleteIntegration}
                    onToggleEditable={toggleEditable}
                />
            ))}
        </div>
    );

    return (
        <MainPageWithButtons
            pageTitle={t("mod_integrations_manual_title")}
            content={body}
            buttons={[
                <AddIntegrationButton
                    integrations={integrationsLocal}
                    isEnabled={canAddOnMore}
                    onClick={addIntegration}
                />,
            ]}
        />
    );
}
